import { useQueryCache } from '@pinia/colada';
import { CancelToken } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { LibraryAPI } from '@/apis';
import * as CampaignCreatorInsightsAPI from '@/apis/campaign-creator-insights';
import {
  excludeCreatorMediaFromCampaign,
  includeCreatorMediaInCampaign,
} from '@/apis/campaign-deliverables';
import * as TikTokAPI from '@/apis/tiktok';
import { sanitizeStoriesFromDMs } from '@/app/creators/utils';
import { useAuthStore } from '@/stores/auth';
import { useCampaignsStore } from '@/stores/campaigns';

export const useCampaignCreatorInsightsStore = defineStore('campaignCreatorInsights', () => {
  const campaignLevelStats = ref({});
  const creatorChannels = ref([]);

  const creatorPosts = ref([]);
  const loadingCreatorPosts = ref(false);
  const creatorPostsCancelToken = ref(null);
  const postsPaging = ref(null);

  const ttcmStats = ref({});

  const loadingCampaignStats = ref(false);
  // sc-175482 will improve the error handling so we can save more than 1 error.
  // But for now, we'll use this separate error property so we can differentiate
  const errorCampaignLevelStats = ref(null);
  const errorTTCMStats = ref(null);

  const loadingTiktokStats = ref(false);
  // sc-175482: create better error storage instead of saving only one error.
  const error = ref(null);

  const queryCache = useQueryCache();

  function invalidateDeliverablesCache() {
    const authStore = useAuthStore();
    const campaignsStore = useCampaignsStore();
    const brandId = authStore.currentBrand.id;
    const campaignId = campaignsStore.campaign.id;

    queryCache.invalidateQueries({
      key: [brandId, campaignId, 'campaign-deliverables'],
      exact: false,
    });
  }

  async function fetchCampaignLevelStats() {
    const authStore = useAuthStore();
    const brandId = authStore.currentBrand.id;

    const campaignsStore = useCampaignsStore();
    const campaignId = campaignsStore.campaign.id;

    try {
      loadingCampaignStats.value = true;
      errorCampaignLevelStats.value = null;

      const response = await CampaignCreatorInsightsAPI.getCampaignLevelStats({
        brandId,
        campaignId,
      });

      campaignLevelStats.value = response.data.data;
      creatorChannels.value = response.data.data.creatorChannels;
    } catch (err) {
      errorCampaignLevelStats.value = err;
    } finally {
      loadingCampaignStats.value = false;
    }
  }

  async function fetchCampaignCreatorsTikTokStats() {
    const authStore = useAuthStore();
    const brandId = authStore.currentBrand.id;

    const campaignsStore = useCampaignsStore();
    const campaign = campaignsStore.campaign;
    const campaignCode = campaign.ttcm_orders_campaign_code;

    try {
      loadingTiktokStats.value = true;
      errorTTCMStats.value = null;
      const response = await TikTokAPI.getCampaignCreatorsStats({
        brandId,
        campaignCode,
      });

      ttcmStats.value = response.data.data;
    } catch (err) {
      errorTTCMStats.value = err;
    } finally {
      loadingTiktokStats.value = false;
    }
  }

  async function includeCampaignCreatorPosts(posts, filterOutPosts = true) {
    const authStore = useAuthStore();
    const brandId = authStore.currentBrand.id;

    const campaignsStore = useCampaignsStore();
    const campaign = campaignsStore.campaign;

    const mediaIds = posts.map((post) => post.id);
    let originalPosts = [...creatorPosts.value];
    const tempPaging = postsPaging.value;
    error.value = null;
    try {
      loadingCreatorPosts.value = true;
      creatorPosts.value = [];
      postsPaging.value = null;

      await includeCreatorMediaInCampaign({
        brandId,
        campaignId: campaign.id,
        mediaIds,
      });

      if (campaign?.creator_filters?.excluded_media_ids) {
        campaign.creator_filters.excluded_media_ids =
          campaign.creator_filters.excluded_media_ids.filter((id) => !mediaIds.includes(id));
      }

      if (filterOutPosts) {
        originalPosts = originalPosts.filter((post) => !mediaIds.includes(post.id));
      }

      await fetchCampaignLevelStats();
      if (campaign.ttcm_orders_campaign_code) {
        await fetchCampaignCreatorsTikTokStats();
      }

      invalidateDeliverablesCache();
    } catch (err) {
      // Revert the optimistic update on error
      if (campaign?.creator_filters?.excluded_media_ids) {
        campaign.creator_filters.excluded_media_ids = [
          ...campaign.creator_filters.excluded_media_ids,
          ...mediaIds,
        ];
      }
      error.value = err;
    } finally {
      creatorPosts.value = originalPosts;
      loadingCreatorPosts.value = false;
      postsPaging.value = tempPaging;
    }
  }

  async function excludeCampaignCreatorPosts(posts, filterOutPosts = true) {
    const authStore = useAuthStore();
    const brandId = authStore.currentBrand.id;

    const campaignsStore = useCampaignsStore();
    const campaign = campaignsStore.campaign;

    const mediaIds = posts.map((post) => post.id);
    let originalPosts = [...creatorPosts.value];
    const tempPaging = postsPaging.value;
    error.value = null;
    try {
      loadingCreatorPosts.value = true;
      creatorPosts.value = [];
      postsPaging.value = null;

      if (campaign?.creator_filters) {
        campaign.creator_filters.excluded_media_ids = [
          ...(campaign.creator_filters.excluded_media_ids || []),
          ...mediaIds,
        ];
      }

      await excludeCreatorMediaFromCampaign({
        brandId,
        campaignId: campaign.id,
        mediaIds,
      });
      if (filterOutPosts) {
        originalPosts = originalPosts.filter((post) => !mediaIds.includes(post.id));
      }
      await fetchCampaignLevelStats();
      if (campaign.ttcm_orders_campaign_code) {
        await fetchCampaignCreatorsTikTokStats();
      }

      invalidateDeliverablesCache();
    } catch (err) {
      if (campaign?.creator_filters) {
        campaign.creator_filters.excluded_media_ids =
          campaign.creator_filters.excluded_media_ids.filter((id) => !mediaIds.includes(id));
      }
      error.value = err;
    } finally {
      creatorPosts.value = originalPosts;
      loadingCreatorPosts.value = false;
      postsPaging.value = tempPaging;
    }
  }

  async function getCreatorCampaignPosts(
    { mediaTypes, sort, postFilter, youtube },
    url = null,
    refresh = true,
    limit = 20,
  ) {
    const authStore = useAuthStore();
    const brandId = authStore.currentBrand.id;
    const campaignsStore = useCampaignsStore();
    const campaign = campaignsStore.campaign;
    const sourceCreatedAt = campaign.creator_filters?.source_created_at || undefined;
    if (refresh) creatorPosts.value = [];

    creatorPostsCancelToken.value?.cancel();
    creatorPostsCancelToken.value = CancelToken.source();
    try {
      loadingCreatorPosts.value = true;
      const libraryResponse = await LibraryAPI.getMediaListV2(
        {
          brandId,
          limit,
          offset: 0,
          filters: {
            campaignIds: [campaign.id],
            brandMediaTypes: mediaTypes,
            sourceCreatedAt,
            includeDisconnectedStoryUgc: true,
            ...(youtube && { youtube }),
          },
          sortFields: sort,
          includeCreator: true,
          url,
          campaignCreatorFiltersOptions: {
            showExcludedMedia: postFilter !== 'INCLUDED',
            onlyExcludedMedia: postFilter === 'EXCLUDED',
          },
        },
        { cancelToken: creatorPostsCancelToken.value.token },
      );
      creatorPosts.value = sanitizeStoriesFromDMs([
        ...creatorPosts.value,
        ...(libraryResponse?.data?.data || []),
      ]);
      postsPaging.value = libraryResponse?.data?.paging?.next;
    } catch (err) {
      error.value = err;
    } finally {
      loadingCreatorPosts.value = false;
    }
  }

  const creatorStats = ref([]);
  const loadingCreatorStats = ref(false);

  const creatorStatsSortOrder = ref('DESC');
  const creatorStatsSortField = ref('TOTAL_EMV');
  const creatorStatsSortString = computed(
    () => `${creatorStatsSortOrder.value === 'DESC' ? '-' : ''}${creatorStatsSortField.value}`,
  );
  const creatorStatsLookupName = ref(undefined);
  const creatorStatsRestrictToPostTypes = ref([]);

  const creatorStatsAfterCursor = ref(undefined);

  async function fetchCreatorLevelStats(refresh = true) {
    const authStore = useAuthStore();
    const brandId = authStore.currentBrand.id;

    const campaignsStore = useCampaignsStore();
    const campaign = campaignsStore.campaign;
    const creatorIds = campaign.creator_ids;

    const restrictToPlatforms = [
      ...new Set(creatorStatsRestrictToPostTypes.value.map((postType) => postType.split('_')[0])),
    ];
    const postTypes = [
      ...(creatorStatsRestrictToPostTypes.value.includes('INSTAGRAM_FEED_OR_REELS')
        ? ['IMAGE', 'VIDEO', 'CAROUSEL', 'FEED_POSTS']
        : []),
      ...(creatorStatsRestrictToPostTypes.value.includes('INSTAGRAM_STORY') ? ['STORY'] : []),
    ];

    if (refresh) {
      creatorStats.value = [];
      creatorStatsAfterCursor.value = undefined;
    }

    try {
      loadingCreatorStats.value = true;
      const response = await CampaignCreatorInsightsAPI.getCreatorLevelStats({
        brandId,
        creatorIds,
        postsPublishedStartDate: campaign.creator_filters?.source_created_at?.start,
        postsPublishedEndDate: campaign.creator_filters?.source_created_at?.end,
        sort: creatorStatsSortString.value,
        restrictToPlatforms: restrictToPlatforms.length === 1 ? restrictToPlatforms : undefined,
        postTypes: postTypes.length > 0 && postTypes.length < 4 ? postTypes : undefined,
        lookupName: creatorStatsLookupName.value,
        afterCursor: creatorStatsAfterCursor.value,
      });

      creatorStats.value = [...creatorStats.value, ...(response.data.data || [])];
      creatorStatsAfterCursor.value = response.data.paging.nextCursor;
    } catch {
      loadingCreatorStats.value = false;
    } finally {
      loadingCreatorStats.value = false;
    }
  }

  watch(
    [
      () => creatorStatsRestrictToPostTypes.value,
      () => creatorStatsLookupName.value,
      () => creatorStatsSortString.value,
    ],
    () => {
      fetchCreatorLevelStats();
    },
    { immediate: false },
  );

  return {
    campaignLevelStats,
    loadingCampaignStats,
    loadingTiktokStats,
    error,
    errorCampaignLevelStats,
    errorTTCMStats,
    creatorChannels,
    fetchCampaignLevelStats,
    fetchCampaignCreatorsTikTokStats,
    getCreatorCampaignPosts,
    creatorPosts,
    postsPaging,
    loadingCreatorPosts,
    creatorStatsSortOrder,
    creatorStatsSortField,
    creatorStatsLookupName,
    fetchCreatorLevelStats,
    creatorStats,
    loadingCreatorStats,
    creatorStatsSortString,
    creatorStatsRestrictToPostTypes,
    creatorStatsAfterCursor,
    ttcmStats,
    includeCampaignCreatorPosts,
    excludeCampaignCreatorPosts,
  };
});
